'use strict';

import { getTokenFromLocalStorage } from '@/utils/auth0Token';
import { API_BASEPATH_PORTAL } from '@/config/env.config.js';
import { portalApi } from './index.api';

function studyBuilderV10AbsoluteUrl(path) {
	const base = String(API_BASEPATH_PORTAL || '').replace(/\/?$/, '');
	if (!base) return `/api/v1.0/study-builder${path}`;
	const withoutV1 = base.replace(/\/api\/v1\/?$/, '');
	return `${withoutV1}/api/v1.0/study-builder${path}`;
}

/** POST /api/v1.0/study-builder — absolute URL so it is not tied to the portal client base path version. */
export function studyBuilderSubmitAbsoluteUrl() {
	return studyBuilderV10AbsoluteUrl('');
}

/** POST /api/v1.0/study-builder/estimate */
export function studyBuilderEstimateAbsoluteUrl() {
	return studyBuilderV10AbsoluteUrl('/estimate');
}

/** POST /api/v1.0/study-builder/draft */
export function studyBuilderDraftAbsoluteUrl() {
	return studyBuilderV10AbsoluteUrl('/draft');
}

/** GET /api/v1.0/study-builder/estimate/latest */
export function studyBuilderEstimateLatestAbsoluteUrl() {
	return studyBuilderV10AbsoluteUrl('/estimate/latest');
}

function newIdempotencyKey() {
	try {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
	} catch {
		// ignore
	}
	return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Emails the submitter the study summary + indicative estimate (plain text).
 * POST /api/v1.0/study-builder — body: { toEmail, contact, plainTextBody }; optional Idempotency-Key header.
 *
 * Authorization is omitted unless the app considers the user logged in. Sending a stale/expired Bearer
 * token would make ASP.NET challenge Auth0 and return 401 before guest handling — so we never attach
 * Bearer unless we have a non-expired access token from the Auth0 cache (same source as the rest of the app).
 */
function studyBuilderAuthHeaders() {
	const token = getTokenFromLocalStorage();
	const headers = {};
	if (token) {
		headers.authorization = `Bearer ${token}`;
	}
	return headers;
}

export const studyBuilderApi = portalApi.injectEndpoints({
	endpoints: (builder) => ({
		postStudyBuilderEstimate: builder.mutation({
			query: (body) => ({
				url: studyBuilderEstimateAbsoluteUrl(),
				method: 'POST',
				headers: studyBuilderAuthHeaders(),
				body,
			}),
		}),
		postStudyBuilderDraft: builder.mutation({
			query: (body) => ({
				url: studyBuilderDraftAbsoluteUrl(),
				method: 'POST',
				headers: studyBuilderAuthHeaders(),
				body,
			}),
		}),
		getStudyBuilderDraft: builder.query({
			query: () => ({
				url: studyBuilderDraftAbsoluteUrl(),
				method: 'GET',
				headers: studyBuilderAuthHeaders(),
			}),
		}),
		getStudyBuilderEstimateLatest: builder.query({
			query: () => ({
				url: studyBuilderEstimateLatestAbsoluteUrl(),
				method: 'GET',
				headers: studyBuilderAuthHeaders(),
			}),
		}),
		sendStudyBuilderQuoteSummaryEmail: builder.mutation({
			query: (body) => {
				const headers = {
					...studyBuilderAuthHeaders(),
					'Idempotency-Key': newIdempotencyKey(),
				};
				const { toEmail, contact, plainTextBody } = body;
				return {
					url: studyBuilderSubmitAbsoluteUrl(),
					method: 'POST',
					headers,
					body: { toEmail, contact, plainTextBody },
				};
			},
		}),
	}),
});

export const {
	usePostStudyBuilderEstimateMutation,
	usePostStudyBuilderDraftMutation,
	useSendStudyBuilderQuoteSummaryEmailMutation,
	useLazyGetStudyBuilderDraftQuery,
	useLazyGetStudyBuilderEstimateLatestQuery,
} = studyBuilderApi;
